import React, { useEffect, useState } from 'react';
import { workflowsService } from './workflowsService';
import {
  WorkflowDefinition,
  WorkflowInstance,
  WorkflowTask,
  WorkflowApproval,
  WorkflowTemplate,
  WorkflowDashboardMetrics,
} from './types';
import { WorkflowMetricsCards } from './components/WorkflowMetricsCards';
import { WorkflowSimulationModal } from './components/WorkflowSimulationModal';
import { useDialog } from '../../shared/DialogContext';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';

export const WorkflowsPage: React.FC = () => {
  const { showError, showSuccess } = useDialog();
  const [metrics, setMetrics] = useState<WorkflowDashboardMetrics | null>(null);
  const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [approvals, setApprovals] = useState<WorkflowApproval[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'definitions' | 'instances' | 'tasks' | 'approvals' | 'templates'>('overview');
  const [isSimulating, setIsSimulating] = useState<boolean>(false);

  const {
    currentPage: instPage,
    setCurrentPage: setInstPage,
    pageSize: instPageSize,
    setPageSize: setInstPageSize,
    totalItems: totalInstItems,
    paginatedItems: paginatedInstances,
  } = useTablePagination(instances, 25);

  const {
    currentPage: taskPage,
    setCurrentPage: setTaskPage,
    pageSize: taskPageSize,
    setPageSize: setTaskPageSize,
    totalItems: totalTaskItems,
    paginatedItems: paginatedTasks,
  } = useTablePagination(tasks, 25);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [m, d, inst, t, a, tmpl] = await Promise.all([
        workflowsService.getDashboardMetrics(),
        workflowsService.getDefinitions(),
        workflowsService.getInstances(),
        workflowsService.getTasks(),
        workflowsService.getApprovals(),
        workflowsService.getTemplates(),
      ]);
      setMetrics(m);
      setDefinitions(d);
      setInstances(inst);
      setTasks(t);
      setApprovals(a);
      setTemplates(tmpl);
    } catch (err) {
      console.error('Failed to load workflows data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimTask = async (taskId: string) => {
    try {
      await workflowsService.claimTask(taskId, 'CURRENT_USER');
      await loadData();
      showSuccess('Workflow task claimed successfully.');
    } catch (err: any) {
      showError(err, 'Task Claim Failed');
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await workflowsService.completeTask(taskId, { note: 'Task executed successfully.' });
      await loadData();
      showSuccess('Workflow task completed.');
    } catch (err: any) {
      showError(err, 'Task Completion Failed');
    }
  };

  const handleApproval = async (approvalId: string, decision: 'APPROVED' | 'REJECTED') => {
    try {
      await workflowsService.recordApprovalDecision(approvalId, decision, 'Formal decision registered.');
      await loadData();
      showSuccess(`Workflow approval decision "${decision}" recorded.`);
    } catch (err: any) {
      showError(err, 'Approval Decision Failed');
    }
  };

  return (
    <div>
      {/* 1. FlowDesk Hero Header Banner */}
      <div className="page-header-banner">
        <div className="badge">PROCESS ORCHESTRATION</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Automated Workflows & State Machines</h1>
            <p>
              Central orchestration engine: state transitions, task dispatch, multi-step approvals (Separation of Duties), and decision simulation runtime.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsSimulating(true)}
              className="btn-primary"
              style={{ width: 'auto', background: '#7c3aed', padding: '10px 18px' }}
            >
              🧪 Workflow Simulator
            </button>
            <button
              onClick={loadData}
              className="btn-secondary"
              style={{ width: 'auto' }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* 2. KPI Metrics Cards */}
      <WorkflowMetricsCards metrics={metrics} />

      {/* 3. Navigation Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('overview')}
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
        >
          📊 Execution Overview
        </button>
        <button
          onClick={() => setActiveTab('definitions')}
          className={`tab-btn ${activeTab === 'definitions' ? 'active' : ''}`}
        >
          📜 Definitions ({definitions.length})
        </button>
        <button
          onClick={() => setActiveTab('instances')}
          className={`tab-btn ${activeTab === 'instances' ? 'active' : ''}`}
        >
          ⚡ Active Processes ({instances.length})
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
        >
          📋 Assigned Tasks ({tasks.length})
        </button>
        <button
          onClick={() => setActiveTab('approvals')}
          className={`tab-btn ${activeTab === 'approvals' ? 'active' : ''}`}
        >
          ✍️ Pending Approvals ({approvals.length})
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
        >
          📁 Templates ({templates.length})
        </button>
      </div>

      {/* 4. Tab Content */}
      <div>
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
              <div className="card" style={{ marginBottom: 0 }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>
                  Active Process Execution Log
                </h3>
                <div className="table-container" style={{ marginBottom: 0 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Module Domain</th>
                        <th>Instance Code</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Initiator</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instances.map((inst) => (
                        <tr key={inst.id}>
                          <td style={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            {inst.business_entity_type}
                          </td>
                          <td style={{ fontFamily: 'monospace' }}>{inst.instance_code}</td>
                          <td>
                            <span className={`badge-pill ${inst.status === 'RUNNING' ? 'badge-info' : 'badge-warning'}`}>
                              {inst.status}
                            </span>
                          </td>
                          <td>
                            <span className={`badge-pill ${
                              inst.priority === 'HIGH' || inst.priority === 'CRITICAL' ? 'badge-danger' : 'badge-neutral'
                            }`}>
                              {inst.priority}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: '#64748b' }}>{inst.started_by}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Architectural Principles Card */}
              <div className="card" style={{ marginBottom: 0, background: '#f8fafc' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
                  🛡️ Core Workflow Architecture
                </h3>
                <ul style={{ fontSize: '12px', color: '#475569', lineHeight: '1.6', paddingLeft: '18px' }}>
                  <li><strong>Workflow State ≠ Domain Status:</strong> The engine orchestrates transitions; domain records retain their own business states.</li>
                  <li><strong>Controlled Action Handlers:</strong> Arbitrary scripts are prohibited; only registered operations (CREATE_TASK, CHANGE_STATUS) are executed.</li>
                  <li><strong>Separation of Duties (SoD):</strong> A record author or submitter cannot approve their own transition gate (Creator ≠ Approver).</li>
                  <li><strong>Version Immutability:</strong> Published definitions are locked; running instances conclude on their initial version specification.</li>
                </ul>
              </div>
            </div>
          )}

          {/* TAB 2: DEFINITIONS */}
          {activeTab === 'definitions' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Workflow Name</th>
                    <th>Module</th>
                    <th>Entity Type</th>
                    <th>Status</th>
                    <th>System Locked</th>
                  </tr>
                </thead>
                <tbody>
                  {definitions.map((def) => (
                    <tr key={def.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{def.code}</td>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{def.name}</td>
                      <td>
                        <span className="badge-pill badge-info">
                          {def.module_code}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#475569' }}>{def.entity_type}</td>
                      <td>
                        <span className="badge-pill badge-success">{def.status}</span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {def.is_system ? 'System Protected' : 'Custom'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 3: INSTANCES */}
          {activeTab === 'instances' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Workflow Code</th>
                    <th>Entity Type</th>
                    <th>Entity Reference</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Started At</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInstances.map((inst) => (
                    <tr key={inst.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>
                        {inst.instance_code}
                      </td>
                      <td style={{ fontWeight: 600 }}>{inst.business_entity_type}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{inst.business_entity_id}</td>
                      <td>
                        <span className="badge-pill badge-info">
                          {inst.status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-pill ${
                          inst.priority === 'HIGH' || inst.priority === 'CRITICAL' ? 'badge-danger' : 'badge-neutral'
                        }`}>
                          {inst.priority}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>{inst.started_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                currentPage={instPage}
                totalItems={totalInstItems}
                pageSize={instPageSize}
                onPageChange={setInstPage}
                onPageSizeChange={setInstPageSize}
                itemLabel="instances"
              />
            </div>
          )}

          {/* TAB 4: TASKS */}
          {activeTab === 'tasks' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Task Title</th>
                    <th>Role / Assignee</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTasks.map((tsk) => (
                    <tr key={tsk.id}>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{tsk.title}</td>
                      <td style={{ fontSize: '12px', color: '#475569' }}>
                        {tsk.assigned_user_id || tsk.assigned_role_id || 'Unassigned'}
                      </td>
                      <td>
                        <span className={`badge-pill ${
                          tsk.priority === 'HIGH' || tsk.priority === 'CRITICAL' ? 'badge-danger' : 'badge-neutral'
                        }`}>
                          {tsk.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-pill ${tsk.status === 'COMPLETED' ? 'badge-success' : 'badge-warning'}`}>
                          {tsk.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {tsk.status !== 'COMPLETED' && (
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            {!tsk.assigned_user_id && (
                              <button
                                onClick={() => handleClaimTask(tsk.id)}
                                className="btn-secondary"
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                              >
                                Claim
                              </button>
                            )}
                            <button
                              onClick={() => handleCompleteTask(tsk.id)}
                              className="btn-primary"
                              style={{ width: 'auto', padding: '4px 10px', fontSize: '12px', background: '#10b981' }}
                            >
                              Complete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                currentPage={taskPage}
                totalItems={totalTaskItems}
                pageSize={taskPageSize}
                onPageChange={setTaskPage}
                onPageSizeChange={setTaskPageSize}
                itemLabel="tasks"
              />
            </div>
          )}

          {/* TAB 5: APPROVALS */}
          {activeTab === 'approvals' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Approval Title</th>
                    <th>Approval Type</th>
                    <th>Required / Done</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Decisions</th>
                  </tr>
                </thead>
                <tbody>
                  {approvals.map((appr) => (
                    <tr key={appr.id}>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{appr.title}</td>
                      <td style={{ fontSize: '12px', color: '#475569' }}>{appr.approval_type}</td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {appr.completed_count} / {appr.required_count}
                      </td>
                      <td>
                        <span className={`badge-pill ${appr.status === 'APPROVED' ? 'badge-success' : 'badge-warning'}`}>
                          {appr.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {appr.status === 'PENDING' && (
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button
                              onClick={() => handleApproval(appr.id, 'APPROVED')}
                              className="btn-primary"
                              style={{ width: 'auto', padding: '4px 12px', fontSize: '12px', background: '#10b981' }}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleApproval(appr.id, 'REJECTED')}
                              className="btn-secondary"
                              style={{ padding: '4px 12px', fontSize: '12px', color: '#dc2626', borderColor: '#fca5a5' }}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 6: TEMPLATES */}
          {activeTab === 'templates' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div className="card" style={{ marginBottom: 0 }}>
                <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                  Standard Occurrence Investigation Pipeline
                </h4>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                  Formal investigation workflow, evidence gathering, root-cause signoff, and Safety Director multi-gate closure.
                </p>
                <span className="badge-pill badge-neutral">Trigger: OCCURRENCE_RECORDED</span>
              </div>
              <div className="card" style={{ marginBottom: 0 }}>
                <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                  Critical CAPA Multi-Gate Approval
                </h4>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                  Root-cause verification, corrective implementation tracking, and post-action effectiveness audit signoff.
                </p>
                <span className="badge-pill badge-neutral">Trigger: FINDING_CRITICAL</span>
              </div>
            </div>
          )}
      </div>

      {/* Simulation Modal */}
      {isSimulating && (
        <WorkflowSimulationModal
          definitions={definitions}
          onClose={() => setIsSimulating(false)}
        />
      )}
    </div>
  );
};

export default WorkflowsPage;
